/**
 * useImageEditor - Hook for image editing operations
 * Features: Background removal using HuggingFace ISNet (default: isnet_general)
 */

import { useState, useCallback } from "react";

import axios from "axios";

import { auth } from "@/lib/firebase";

export interface ImageEditResult {
  id: string;
  savedLocally?: boolean;
  downloadUrl?: string;
  operation: "remove-background";
}

interface UseImageEditorProps {
  projectId: string;
  devContainerId?: string; // For local save mode
}

interface UseImageEditorReturn {
  removeBackground: (imageUrl: string) => Promise<ImageEditResult>;
  isProcessing: boolean;
  result: ImageEditResult | null;
  error: string | null;
  reset: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

export function useImageEditor({
  projectId,
  devContainerId,
}: UseImageEditorProps): UseImageEditorReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ImageEditResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setIsProcessing(false);
  }, []);

  // Helper to check if a string is a valid URL
  const isValidUrl = (str: string): boolean => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const removeBackground = useCallback(
    async (imageUrl: string): Promise<ImageEditResult> => {
      // Validate URL format before sending
      if (!isValidUrl(imageUrl)) {
        throw new Error(`Invalid image URL format: ${imageUrl}`);
      }

      setIsProcessing(true);
      setError(null);

      try {
        const token = await auth.getIdToken();
        if (!token) {
          throw new Error("Authentication required");
        }

        const response = await axios.post(
          `${API_URL}/api/image-generation/remove-background`,
          {
            projectId,
            imageUrl,
            ...(devContainerId && { devContainerId }),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const editResult: ImageEditResult = {
          id: response.data.id,
          savedLocally: response.data.savedLocally,
          downloadUrl: response.data.downloadUrl,
          operation: "remove-background",
        };

        setResult(editResult);
        return editResult;
      } catch (err: unknown) {
        let errorMessage = "Failed to remove background";
        if (axios.isAxiosError(err)) {
          errorMessage = err.response?.data?.message || errorMessage;
        }
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    },
    [projectId, devContainerId]
  );

  return {
    removeBackground,
    isProcessing,
    result,
    error,
    reset,
  };
}
